import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getSupplierpoCCForAmend,
    getAllSupplierPOForAmend,
    getItemsForPOAmend,
    getUploadDocSupplierAmendValue,
    getPOUploadedDocs,
    checkBudgetForSuplrPOAmend,
    saveSupplierPOAmend,
    getPreviousePODetails,
    uploadSupplierPOAmendDoc,
} from '../../api/PurchaseAPI/supplierPOAmendAPI';

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchAmendCCList = createAsyncThunk(
    'supplierPOAmendCreation/fetchCCList',
    async ({ userId, roleId, ccType }, { rejectWithValue }) => {
        try {
            const res = await getSupplierpoCCForAmend(userId, roleId, ccType);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

export const fetchAmendPOList = createAsyncThunk(
    'supplierPOAmendCreation/fetchPOList',
    async ({ ccCode, userId, roleId }, { rejectWithValue }) => {
        try {
            const res = await getAllSupplierPOForAmend(ccCode, userId, roleId);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

export const fetchAmendItemGrid = createAsyncThunk(
    'supplierPOAmendCreation/fetchItemGrid',
    async ({ ccCode, poNo }, { rejectWithValue }) => {
        try {
            const res = await getItemsForPOAmend(ccCode, poNo);
            return res.data?.Data || null;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

export const fetchAmendUploadDocValue = createAsyncThunk(
    'supplierPOAmendCreation/fetchUploadDocValue',
    async (ccCode, { rejectWithValue }) => {
        try {
            const res = await getUploadDocSupplierAmendValue(ccCode);
            const rows = res.data?.Data || [];
            return rows[0]?.uploadvalue ?? 0;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

export const fetchAmendUploadedDocs = createAsyncThunk(
    'supplierPOAmendCreation/fetchUploadedDocs',
    async ({ poNo, forType }, { rejectWithValue }) => {
        try {
            const res = await getPOUploadedDocs(poNo, forType);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

export const checkAmendBudget = createAsyncThunk(
    'supplierPOAmendCreation/checkBudget',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await checkBudgetForSuplrPOAmend(payload);
            return res.data?.Data ?? res.data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

export const submitSupplierPOAmend = createAsyncThunk(
    'supplierPOAmendCreation/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await saveSupplierPOAmend(payload);
            return res.data?.Data ?? res.data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

export const fetchAmendPreviousPODetails = createAsyncThunk(
    'supplierPOAmendCreation/fetchPreviousPODetails',
    async (itemCode, { rejectWithValue }) => {
        try {
            const res = await getPreviousePODetails(itemCode);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

export const uploadAmendDocument = createAsyncThunk(
    'supplierPOAmendCreation/uploadDocument',
    async ({ file, fileName, folder, onProgress }, { rejectWithValue }) => {
        try {
            const res = await uploadSupplierPOAmendDoc(file, fileName, folder, onProgress);
            return res.data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message);
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    poList: [],
    itemGrid: null,          // { POData, lstItems, SerialNo, MRRExist }
    uploadDocValue: 0,
    uploadedDocs: [],
    budgetResult: null,
    saveResult: null,
    previousPODetails: [],
    loading: {
        ccList: false,
        poList: false,
        itemGrid: false,
        uploadDocValue: false,
        uploadedDocs: false,
        budget: false,
        save: false,
        previousPODetails: false,
        uploadDocument: false,
    },
    errors: {
        ccList: null,
        poList: null,
        itemGrid: null,
        budget: null,
        save: null,
    },
};

const supplierPOAmendCreationSlice = createSlice({
    name: 'supplierPOAmendCreation',
    initialState,
    reducers: {
        clearAmendPOList:      (s) => { s.poList = []; },
        clearAmendItemGrid:    (s) => { s.itemGrid = null; s.errors.itemGrid = null; },
        clearAmendUploadedDocs:(s) => { s.uploadedDocs = []; },
        clearAmendBudgetResult:(s) => { s.budgetResult = null; s.errors.budget = null; },
        clearAmendSaveResult:  (s) => { s.saveResult = null; s.errors.save = null; },
        clearPreviousPODetails:(s) => { s.previousPODetails = []; },
        resetSupplierPOAmendCreation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAmendCCList.pending,   s => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchAmendCCList.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchAmendCCList.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; s.ccList = []; });

        builder
            .addCase(fetchAmendPOList.pending,   s => { s.loading.poList = true; s.errors.poList = null; })
            .addCase(fetchAmendPOList.fulfilled, (s, a) => { s.loading.poList = false; s.poList = a.payload; })
            .addCase(fetchAmendPOList.rejected,  (s, a) => { s.loading.poList = false; s.errors.poList = a.payload; s.poList = []; });

        builder
            .addCase(fetchAmendItemGrid.pending,   s => { s.loading.itemGrid = true; s.errors.itemGrid = null; s.itemGrid = null; })
            .addCase(fetchAmendItemGrid.fulfilled, (s, a) => { s.loading.itemGrid = false; s.itemGrid = a.payload; })
            .addCase(fetchAmendItemGrid.rejected,  (s, a) => { s.loading.itemGrid = false; s.errors.itemGrid = a.payload; });

        builder
            .addCase(fetchAmendUploadDocValue.pending,   s => { s.loading.uploadDocValue = true; })
            .addCase(fetchAmendUploadDocValue.fulfilled, (s, a) => { s.loading.uploadDocValue = false; s.uploadDocValue = a.payload; })
            .addCase(fetchAmendUploadDocValue.rejected,  s => { s.loading.uploadDocValue = false; s.uploadDocValue = 0; });

        builder
            .addCase(fetchAmendUploadedDocs.pending,   s => { s.loading.uploadedDocs = true; })
            .addCase(fetchAmendUploadedDocs.fulfilled, (s, a) => { s.loading.uploadedDocs = false; s.uploadedDocs = a.payload; })
            .addCase(fetchAmendUploadedDocs.rejected,  s => { s.loading.uploadedDocs = false; s.uploadedDocs = []; });

        builder
            .addCase(checkAmendBudget.pending,   s => { s.loading.budget = true; s.errors.budget = null; s.budgetResult = null; })
            .addCase(checkAmendBudget.fulfilled, (s, a) => { s.loading.budget = false; s.budgetResult = a.payload; })
            .addCase(checkAmendBudget.rejected,  (s, a) => { s.loading.budget = false; s.errors.budget = a.payload; });

        builder
            .addCase(submitSupplierPOAmend.pending,   s => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitSupplierPOAmend.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitSupplierPOAmend.rejected,  (s, a) => { s.loading.save = false; s.errors.save = a.payload; });

        builder
            .addCase(fetchAmendPreviousPODetails.pending,   s => { s.loading.previousPODetails = true; s.previousPODetails = []; })
            .addCase(fetchAmendPreviousPODetails.fulfilled, (s, a) => { s.loading.previousPODetails = false; s.previousPODetails = a.payload; })
            .addCase(fetchAmendPreviousPODetails.rejected,  s => { s.loading.previousPODetails = false; s.previousPODetails = []; });

        builder
            .addCase(uploadAmendDocument.pending,   s => { s.loading.uploadDocument = true; })
            .addCase(uploadAmendDocument.fulfilled, s => { s.loading.uploadDocument = false; })
            .addCase(uploadAmendDocument.rejected,  s => { s.loading.uploadDocument = false; });
    },
});

export const {
    clearAmendPOList,
    clearAmendItemGrid,
    clearAmendUploadedDocs,
    clearAmendBudgetResult,
    clearAmendSaveResult,
    clearPreviousPODetails,
    resetSupplierPOAmendCreation,
} = supplierPOAmendCreationSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectAmendCCList           = s => s.supplierPOAmendCreation.ccList;
export const selectAmendPOList           = s => s.supplierPOAmendCreation.poList;
export const selectAmendItemGrid         = s => s.supplierPOAmendCreation.itemGrid;
export const selectAmendUploadDocValue   = s => s.supplierPOAmendCreation.uploadDocValue;
export const selectAmendUploadedDocs     = s => s.supplierPOAmendCreation.uploadedDocs;
export const selectAmendBudgetResult     = s => s.supplierPOAmendCreation.budgetResult;
export const selectAmendSaveResult       = s => s.supplierPOAmendCreation.saveResult;
export const selectAmendPreviousPODetails= s => s.supplierPOAmendCreation.previousPODetails;
export const selectAmendCreationLoading  = s => s.supplierPOAmendCreation.loading;
export const selectAmendCreationErrors   = s => s.supplierPOAmendCreation.errors;

export default supplierPOAmendCreationSlice.reducer;
